import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as CalendarIcon, Plus, Edit, Trash2, Send, Users, Gift, TrendingUp, Award } from 'lucide-react';
import './AdventCalendar.css';

const AdventCalendar = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [winners, setWinners] = useState([]);
  const [subscriberStreaks, setSubscriberStreaks] = useState([]);
  const [samples, setSamples] = useState([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns);
        if (data.campaigns.length > 0 && !selectedCampaign) {
          setSelectedCampaign(data.campaigns[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, [API_URL, selectedCampaign]);

  // Fetch milestones
  const fetchMilestones = useCallback(async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/campaigns/${campaignId}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMilestones(data.milestones);
      }
    } catch (error) {
      console.error('Error fetching milestones:', error);
    }
  }, [API_URL]);

  // Fetch winners
  const fetchWinners = useCallback(async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/campaigns/${campaignId}/winners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setWinners(data.winners);
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
    }
  }, [API_URL]);

  // Fetch subscriber streaks
  const fetchSubscriberStreaks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/engagement/streaks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSubscriberStreaks(data.subscribers);
      }
    } catch (error) {
      console.error('Error fetching subscriber streaks:', error);
    }
  }, [API_URL]);

  // Fetch samples for dropdown
  const fetchSamples = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/samples?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSamples(data.samples);
      }
    } catch (error) {
      console.error('Error fetching samples:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchCampaigns();
    fetchSamples();
    fetchSubscriberStreaks();
  }, [fetchCampaigns, fetchSamples, fetchSubscriberStreaks]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchMilestones(selectedCampaign._id);
      fetchWinners(selectedCampaign._id);
    }
  }, [selectedCampaign, fetchMilestones, fetchWinners]);

  // Campaign CRUD
  const createOrUpdateCampaign = async (campaignData) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingCampaign
        ? `${API_URL}/api/advent/campaigns/${editingCampaign._id}`
        : `${API_URL}/api/advent/campaigns`;
      const method = editingCampaign ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(campaignData)
      });

      const data = await response.json();
      if (data.success) {
        alert(editingCampaign ? 'Campaign updated!' : 'Campaign created!');
        fetchCampaigns();
        setShowCampaignModal(false);
        setEditingCampaign(null);
      } else {
        alert(data.message || 'Error saving campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Error saving campaign');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign and all its milestones? This cannot be undone.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        alert('Campaign deleted');
        fetchCampaigns();
        if (selectedCampaign?._id === campaignId) {
          setSelectedCampaign(null);
        }
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };

  // Milestone CRUD
  const createOrUpdateMilestone = async (milestoneData) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingMilestone
        ? `${API_URL}/api/advent/milestones/${editingMilestone._id}`
        : `${API_URL}/api/advent/campaigns/${selectedCampaign._id}/milestones`;
      const method = editingMilestone ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(milestoneData)
      });

      const data = await response.json();
      if (data.success) {
        alert(editingMilestone ? 'Milestone updated!' : 'Milestone created!');
        fetchMilestones(selectedCampaign._id);
        setShowMilestoneModal(false);
        setEditingMilestone(null);
      } else {
        alert(data.message || 'Error saving milestone');
      }
    } catch (error) {
      console.error('Error saving milestone:', error);
      alert('Error saving milestone');
    }
  };

  const deleteMilestone = async (milestoneId) => {
    if (!window.confirm('Delete this milestone?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/milestones/${milestoneId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        alert('Milestone deleted');
        fetchMilestones(selectedCampaign._id);
      }
    } catch (error) {
      console.error('Error deleting milestone:', error);
    }
  };

  // Send prizes
  const sendPrizes = async (milestoneId) => {
    if (!window.confirm('Send prize emails to all eligible subscribers?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/advent/milestones/${milestoneId}/send-prizes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      if (data.success) {
        alert(`Prize emails sent to ${data.results.sent} subscribers!`);
        fetchMilestones(selectedCampaign._id);
        fetchWinners(selectedCampaign._id);
      } else {
        alert(data.message || 'Error sending prizes');
      }
    } catch (error) {
      console.error('Error sending prizes:', error);
      alert('Error sending prizes');
    }
  };

  return (
    <div className="advent-calendar-page">
      <div className="page-header">
        <div className="header-content">
          <CalendarIcon size={32} />
          <div>
            <h1>Advent Calendar Campaigns</h1>
            <p>Manage engagement-based prize campaigns with milestone tracking</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditingCampaign(null); setShowCampaignModal(true); }}>
          <Plus size={20} />
          New Campaign
        </button>
      </div>

      {/* Campaign Selector */}
      <div className="campaign-selector">
        <label>Active Campaign:</label>
        <select
          value={selectedCampaign?._id || ''}
          onChange={(e) => {
            const campaign = campaigns.find(c => c._id === e.target.value);
            setSelectedCampaign(campaign);
          }}
        >
          <option value="">Select a campaign...</option>
          {campaigns.map(campaign => (
            <option key={campaign._id} value={campaign._id}>
              {campaign.name} {campaign.isActive ? '(Active)' : '(Inactive)'}
            </option>
          ))}
        </select>
      </div>

      {selectedCampaign && (
        <>
          {/* Campaign Info Card */}
          <div className="campaign-info-card">
            <div className="info-header">
              <h2>{selectedCampaign.name}</h2>
              <div className="info-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setEditingCampaign(selectedCampaign); setShowCampaignModal(true); }}
                >
                  <Edit size={16} />
                  Edit
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => deleteCampaign(selectedCampaign._id)}
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
            <div className="info-details">
              <div className="info-item">
                <span className="label">Start Date:</span>
                <span className="value">{new Date(selectedCampaign.startDate).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <span className="label">End Date:</span>
                <span className="value">{new Date(selectedCampaign.endDate).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <span className="label">Status:</span>
                <span className={`badge ${selectedCampaign.isActive ? 'badge-success' : 'badge-secondary'}`}>
                  {selectedCampaign.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Auto-Send:</span>
                <span className={`badge ${selectedCampaign.autoSendEnabled ? 'badge-success' : 'badge-warning'}`}>
                  {selectedCampaign.autoSendEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'campaigns' ? 'active' : ''}`}
              onClick={() => setActiveTab('campaigns')}
            >
              <Gift size={18} />
              Milestones
            </button>
            <button
              className={`tab ${activeTab === 'winners' ? 'active' : ''}`}
              onClick={() => setActiveTab('winners')}
            >
              <Award size={18} />
              Winners
            </button>
            <button
              className={`tab ${activeTab === 'streaks' ? 'active' : ''}`}
              onClick={() => setActiveTab('streaks')}
            >
              <TrendingUp size={18} />
              Subscriber Streaks
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'campaigns' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Campaign Milestones</h3>
                <button className="btn btn-primary btn-sm" onClick={() => { setEditingMilestone(null); setShowMilestoneModal(true); }}>
                  <Plus size={16} />
                  Add Milestone
                </button>
              </div>

              <div className="milestones-grid">
                {milestones.length === 0 ? (
                  <div className="empty-state">
                    <CalendarIcon size={48} />
                    <p>No milestones yet. Create your first milestone to start the campaign!</p>
                  </div>
                ) : (
                  milestones.map(milestone => (
                    <MilestoneCard
                      key={milestone._id}
                      milestone={milestone}
                      onEdit={() => { setEditingMilestone(milestone); setShowMilestoneModal(true); }}
                      onDelete={() => deleteMilestone(milestone._id)}
                      onSendPrizes={() => sendPrizes(milestone._id)}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'winners' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Prize Winners</h3>
              </div>

              <div className="winners-table">
                {winners.length === 0 ? (
                  <div className="empty-state">
                    <Award size={48} />
                    <p>No winners yet. Prizes will appear here once milestones are reached.</p>
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Subscriber</th>
                        <th>Prize</th>
                        <th>Streak</th>
                        <th>Date Awarded</th>
                        <th>Email Status</th>
                        <th>Claimed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {winners.map(winner => (
                        <tr key={winner._id}>
                          <td>
                            {winner.subscriber?.name || `${winner.subscriber?.firstName} ${winner.subscriber?.lastName}` || winner.subscriber?.email}
                          </td>
                          <td>{winner.milestone?.prizeTitle}</td>
                          <td>
                            <span className="badge badge-info">{winner.streakAtWin} days</span>
                          </td>
                          <td>{new Date(winner.awardedAt).toLocaleDateString()}</td>
                          <td>
                            {winner.emailSent ? (
                              <span className="badge badge-success">Sent</span>
                            ) : (
                              <span className="badge badge-warning">Pending</span>
                            )}
                          </td>
                          <td>
                            {winner.prizeClaimed ? (
                              <span className="badge badge-success">Yes</span>
                            ) : (
                              <span className="badge badge-secondary">No</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {activeTab === 'streaks' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Subscriber Engagement Streaks</h3>
              </div>

              <div className="streaks-table">
                {subscriberStreaks.length === 0 ? (
                  <div className="empty-state">
                    <TrendingUp size={48} />
                    <p>No engagement data yet.</p>
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Subscriber</th>
                        <th>Email</th>
                        <th>Current Streak</th>
                        <th>Longest Streak</th>
                        <th>Last Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriberStreaks.map(subscriber => (
                        <tr key={subscriber._id}>
                          <td>
                            {subscriber.name || `${subscriber.firstName} ${subscriber.lastName}` || '-'}
                          </td>
                          <td>{subscriber.email}</td>
                          <td>
                            <span className="streak-badge">
                              🔥 {subscriber.currentStreak || 0} days
                            </span>
                          </td>
                          <td>
                            <span className="badge badge-info">
                              {subscriber.longestStreak || 0} days
                            </span>
                          </td>
                          <td>
                            {subscriber.lastActivityDate
                              ? new Date(subscriber.lastActivityDate).toLocaleDateString()
                              : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <CampaignModal
          campaign={editingCampaign}
          onClose={() => { setShowCampaignModal(false); setEditingCampaign(null); }}
          onSave={createOrUpdateCampaign}
        />
      )}

      {/* Milestone Modal */}
      {showMilestoneModal && selectedCampaign && (
        <MilestoneModal
          milestone={editingMilestone}
          campaign={selectedCampaign}
          samples={samples}
          onClose={() => { setShowMilestoneModal(false); setEditingMilestone(null); }}
          onSave={createOrUpdateMilestone}
        />
      )}
    </div>
  );
};

// Milestone Card Component
const MilestoneCard = ({ milestone, onEdit, onDelete, onSendPrizes }) => {
  const milestoneDate = new Date(milestone.milestoneDate);
  const isPast = milestoneDate < new Date();
  const eligibleCount = milestone.eligibleCount || 0;

  return (
    <div className={`milestone-card ${milestone.emailsSent ? 'sent' : ''}`}>
      <div className="milestone-date">
        <div className="date-display">
          <span className="day">{milestoneDate.getDate()}</span>
          <span className="month">{milestoneDate.toLocaleDateString('en-US', { month: 'short' })}</span>
        </div>
        <div className="streak-req">
          <span className="label">Requires:</span>
          <span className="value">{milestone.requiredStreak} day streak</span>
        </div>
      </div>

      <div className="milestone-content">
        <h4>{milestone.prizeTitle}</h4>
        <p>{milestone.prizeDescription}</p>

        <div className="milestone-meta">
          <div className="meta-item">
            <Users size={14} />
            <span>{eligibleCount} eligible</span>
          </div>
          <div className="meta-item">
            <Gift size={14} />
            <span className="prize-type">
              {milestone.prizeType === 'special_sample' ? 'Special Sample' : 'External Gift'}
            </span>
          </div>
        </div>

        {milestone.emailsSent && (
          <div className="sent-badge">
            ✓ Prizes Sent ({milestone.recipientCount} recipients)
          </div>
        )}
      </div>

      <div className="milestone-actions">
        <button className="btn btn-secondary btn-sm" onClick={onEdit}>
          <Edit size={14} />
        </button>
        {!milestone.emailsSent && (
          <button
            className="btn btn-primary btn-sm"
            onClick={onSendPrizes}
            disabled={!isPast || eligibleCount === 0}
          >
            <Send size={14} />
            Send Prizes
          </button>
        )}
        <button className="btn btn-danger btn-sm" onClick={onDelete}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// Campaign Modal Component
const CampaignModal = ({ campaign, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: campaign?.name || '',
    description: campaign?.description || '',
    startDate: campaign?.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : '',
    endDate: campaign?.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : '',
    isActive: campaign?.isActive !== undefined ? campaign.isActive : true,
    autoSendEnabled: campaign?.autoSendEnabled !== undefined ? campaign.autoSendEnabled : false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{campaign ? 'Edit Campaign' : 'Create New Campaign'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Campaign Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span>Campaign Active</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.autoSendEnabled}
                onChange={(e) => setFormData({ ...formData, autoSendEnabled: e.target.checked })}
              />
              <span>Auto-Send Prizes (automatically send prizes on milestone dates)</span>
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {campaign ? 'Update' : 'Create'} Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Milestone Modal Component
const MilestoneModal = ({ milestone, campaign, samples, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    milestoneDate: milestone?.milestoneDate ? new Date(milestone.milestoneDate).toISOString().split('T')[0] : '',
    requiredStreak: milestone?.requiredStreak || 1,
    emailSubject: milestone?.emailSubject || '',
    prizeTitle: milestone?.prizeTitle || '',
    prizeDescription: milestone?.prizeDescription || '',
    prizeImage: milestone?.prizeImage || '',
    prizeType: milestone?.prizeType || 'special_sample',
    prizeSample: milestone?.prizeSample?._id || '',
    externalLink: milestone?.externalLink || '',
    buttonText: milestone?.buttonText || 'CLAIM YOUR PRIZE',
    autoSendEnabled: milestone?.autoSendEnabled !== undefined ? milestone.autoSendEnabled : false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{milestone ? 'Edit Milestone' : 'Create New Milestone'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Milestone Date *</label>
              <input
                type="date"
                value={formData.milestoneDate}
                onChange={(e) => setFormData({ ...formData, milestoneDate: e.target.value })}
                min={new Date(campaign.startDate).toISOString().split('T')[0]}
                max={new Date(campaign.endDate).toISOString().split('T')[0]}
                required
              />
              <small>Date when eligible subscribers will receive prizes</small>
            </div>

            <div className="form-group">
              <label>Required Streak (Days) *</label>
              <input
                type="number"
                value={formData.requiredStreak}
                onChange={(e) => setFormData({ ...formData, requiredStreak: parseInt(e.target.value) })}
                min="1"
                required
              />
              <small>Consecutive days of engagement required</small>
            </div>
          </div>

          <div className="form-group">
            <label>Email Subject *</label>
            <input
              type="text"
              value={formData.emailSubject}
              onChange={(e) => setFormData({ ...formData, emailSubject: e.target.value })}
              placeholder="e.g., 🎁 You've Won an Exclusive Prize!"
              required
            />
          </div>

          <div className="form-group">
            <label>Prize Title *</label>
            <input
              type="text"
              value={formData.prizeTitle}
              onChange={(e) => setFormData({ ...formData, prizeTitle: e.target.value })}
              placeholder="e.g., Exclusive Vocal Chop Pack"
              required
            />
          </div>

          <div className="form-group">
            <label>Prize Description *</label>
            <textarea
              value={formData.prizeDescription}
              onChange={(e) => setFormData({ ...formData, prizeDescription: e.target.value })}
              rows="3"
              placeholder="Describe the prize..."
              required
            />
          </div>

          <div className="form-group">
            <label>Prize Image URL</label>
            <input
              type="url"
              value={formData.prizeImage}
              onChange={(e) => setFormData({ ...formData, prizeImage: e.target.value })}
              placeholder="https://example.com/prize-image.jpg"
            />
          </div>

          <div className="form-group">
            <label>Prize Type *</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="prizeType"
                  value="special_sample"
                  checked={formData.prizeType === 'special_sample'}
                  onChange={(e) => setFormData({ ...formData, prizeType: e.target.value })}
                />
                <span>Special Sample (from library)</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="prizeType"
                  value="external_gift"
                  checked={formData.prizeType === 'external_gift'}
                  onChange={(e) => setFormData({ ...formData, prizeType: e.target.value })}
                />
                <span>External Gift (link to external resource)</span>
              </label>
            </div>
          </div>

          {formData.prizeType === 'special_sample' && (
            <div className="form-group">
              <label>Select Sample *</label>
              <select
                value={formData.prizeSample}
                onChange={(e) => setFormData({ ...formData, prizeSample: e.target.value })}
                required
              >
                <option value="">Choose a sample...</option>
                {samples.map(sample => (
                  <option key={sample._id} value={sample._id}>
                    {sample.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.prizeType === 'external_gift' && (
            <div className="form-group">
              <label>External Link *</label>
              <input
                type="url"
                value={formData.externalLink}
                onChange={(e) => setFormData({ ...formData, externalLink: e.target.value })}
                placeholder="https://example.com/gift"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Button Text</label>
            <input
              type="text"
              value={formData.buttonText}
              onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
              placeholder="CLAIM YOUR PRIZE"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.autoSendEnabled}
                onChange={(e) => setFormData({ ...formData, autoSendEnabled: e.target.checked })}
              />
              <span>Auto-send prizes on this date</span>
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {milestone ? 'Update' : 'Create'} Milestone
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdventCalendar;

